import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { CognitoUserService } from '../services/cognito-user.service';
import { CatchitRole } from '../interfaces/environment.interface';

export { CatchitRole };

// Highest to lowest privilege - a role satisfies any required role at or below it.
const ROLE_HIERARCHY: CatchitRole[] = ['super-admin', 'admin', 'user'];

@Injectable({
  providedIn: 'root'
})
export class CatchitAuthGuard implements CanActivate {
  constructor(
    private catchitUserService: CognitoUserService,
    private router: Router
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    // This guard assumes UoA AuthGuard has already verified authentication
    // We handle Catch IT specific role-based access

    // Load current user to check roles
    const user = await this.catchitUserService.loadCurrentUser();
    if (!user) {
      await this.router.navigate(['/error/401']);
      return false;
    }

    // A user who isn't a member of any configured group has no role, and therefore no access
    if (!user.role) {
      await this.router.navigate(['/error/403']);
      return false;
    }

    // role alone isn't sufficient - it can be granted entirely by an external IdP/Grouper
    // entitlement (UoA's own SAML assertion) with zero corresponding Person record in
    // CatchIT's own database. Confirmed live: two real accounts with a valid role but no
    // Person record got past a role-only check into pages that assume a registered user.
    // Only blocks on a *confirmed* 'not-found' - 'unknown' (lookup failed, e.g. offline)
    // deliberately falls through, so a connectivity blip can't lock out an already-registered
    // user who has a real Person record CatchIT just couldn't reach right now.
    if (user.personRecordStatus === 'not-found') {
      await this.router.navigate(['/not-registered']);
      return false;
    }

    // Check role-based access from route data
    const requiredRoles = route.data?.['roles'] as CatchitRole[] | undefined;
    const requiredGroups = route.data?.['groups'] as string[] | undefined;
    const requiredPermissions = route.data?.['permissions'] as string[] | undefined;

    if (requiredRoles && requiredRoles.length > 0) {
      if (!this.checkRoleAccess(user.role, requiredRoles)) {
        await this.router.navigate(['/error/403']);
        return false;
      }
    }

    // Check if user is in required groups. cognitoGroups only - see
    // CognitoUserService.deriveRole() for why customGroups (UoA's own SAML-asserted
    // claim, outside CatchIT's control) is never trusted for access decisions.
    if (requiredGroups && requiredGroups.length > 0) {
      const hasRequiredGroup = requiredGroups.some(group =>
        user.cognitoGroups.includes(group)
      );

      if (!hasRequiredGroup) {
        await this.router.navigate(['/error/403']);
        return false;
      }
    }

    // Additional permission checks
    if (requiredPermissions && requiredPermissions.length > 0) {
      if (!this.checkPermissions(user.role, requiredPermissions)) {
        await this.router.navigate(['/error/403']);
        return false;
      }
    }

    return true;
  }

  /**
   * Check if the user's role satisfies any of the required roles. Higher-privilege
   * roles satisfy checks for lower-privilege roles too (super-admin passes an 'admin' check).
   */
  private checkRoleAccess(userRole: CatchitRole, requiredRoles: CatchitRole[]): boolean {
    const userRank = ROLE_HIERARCHY.indexOf(userRole);
    return requiredRoles.some(required => userRank <= ROLE_HIERARCHY.indexOf(required));
  }

  /**
   * Check if the user's role provides the required permissions
   */
  private checkPermissions(userRole: CatchitRole, requiredPermissions: string[]): boolean {
    // Super admin has all permissions
    if (userRole === 'super-admin') {
      return true;
    }

    // Admin has most permissions except super-admin specific ones
    if (userRole === 'admin') {
      const superAdminOnlyPermissions = ['super-admin-actions', 'system-config'];
      return !requiredPermissions.some(perm => superAdminOnlyPermissions.includes(perm));
    }

    // Regular users have limited permissions
    const userPermissions = ['view-data', 'enter-data', 'manage-own-data'];
    return requiredPermissions.every(perm => userPermissions.includes(perm));
  }
}
